import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { toast, Toaster } from "sonner";
import DashNav from "@/components/chalann/DashNav";
import { getDeliverySettings, updateDeliverySettings, updateDeliveryZones, getStore, getUser } from "@/lib/api";
import { Truck, MessageCircle, Printer, Save, ToggleLeft, ToggleRight, Clock, Phone, Sparkles, Plus, Trash2, MapPin } from "lucide-react";

const LEGEND = "HACEMOS ENVÍOS A TU DOMICILIO EN HORARIOS NORMALES";

/**
 * Ventas a domicilio (WhatsApp orders). Owner-only page. Generates an
 * individual QR per store that opens a pre-filled WhatsApp chat to the
 * store's number. Includes a print-friendly view with the official legend.
 */
export default function Domicilio() {
  const nav = useNavigate();
  const store = getStore();
  const user = getUser();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [phone, setPhone] = useState("");
  const [greeting, setGreeting] = useState("");
  const [schedule, setSchedule] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [zones, setZones] = useState([]);
  const qrRef = useRef(null);

  useEffect(() => {
    if (!store?.id) { nav("/setup"); return; }
    if (!user?.id) { nav("/login"); return; }
    if (user?.role !== "owner") { toast.error("Solo el dueño puede configurar entregas"); nav("/dashboard"); return; }
    (async () => {
      try {
        const s = await getDeliverySettings();
        setSettings(s);
        setEnabled(s.delivery_enabled);
        // Backend guarda con prefijo 52; para no mostrar "+52 523..." al usuario,
        // recortamos el 52 líder cuando el número tiene 12 dígitos MX.
        const raw = (s.whatsapp_number || "").replace(/\D/g, "");
        const display = (raw.length === 12 && raw.startsWith("52")) ? raw.slice(2) : raw;
        setPhone(display);
        setGreeting(s.delivery_greeting || "");
        setSchedule(s.delivery_schedule || "");
        setZones(s.delivery_zones || []);
      } catch { toast.error("No se pudo cargar la configuración"); }
      finally { setLoading(false); }
    })();
  }, [nav, store?.id, user?.id, user?.role]);

  const cleanPhone = useMemo(() => (phone || "").replace(/\D/g, ""), [phone]);
  const waNumber = useMemo(() => {
    if (!cleanPhone) return "";
    return cleanPhone.length === 10 ? `52${cleanPhone}` : cleanPhone;
  }, [cleanPhone]);
  const waUrl = useMemo(() => {
    if (!waNumber) return "";
    const q = encodeURIComponent(greeting || `Hola, quiero un pedido a domicilio de ${store?.name || ""}.`);
    return `https://wa.me/${waNumber}?text=${q}`;
  }, [waNumber, greeting, store?.name]);

  // QR points to the public delivery page (so the client sees the zones list
  // BEFORE writing) whenever the store has at least one zone; otherwise QR
  // opens WhatsApp directly.
  const publicUrl = useMemo(() => {
    if (!store?.id) return "";
    return `${window.location.origin}/entrega/${store.id}`;
  }, [store?.id]);
  const qrTarget = zones.length > 0 && waUrl ? publicUrl : waUrl;

  const save = async () => {
    if (enabled && cleanPhone.length < 10) { toast.error("El WhatsApp debe tener al menos 10 dígitos"); return; }
    setSaving(true);
    try {
      const r = await updateDeliverySettings({
        delivery_enabled: enabled,
        whatsapp_number: cleanPhone,
        delivery_greeting: greeting,
        delivery_schedule: schedule,
      });
      // Save zones too (unified UX)
      const z = await updateDeliveryZones(zones.map((it) => ({
        id: it.id, colonia: it.colonia, price: Number(it.price || 0), min_order: Number(it.min_order || 0), notes: it.notes || "",
      })));
      setSettings({ ...r, delivery_zones: z.delivery_zones });
      setZones(z.delivery_zones);
      toast.success(enabled ? "Servicio a domicilio activado" : "Configuración guardada");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se guardó"); }
    finally { setSaving(false); }
  };

  const addZone = () => setZones((arr) => [...arr, { id: null, colonia: "", price: 30, min_order: 0, notes: "" }]);
  const updateZone = (i, patch) => setZones((arr) => arr.map((z, idx) => idx === i ? { ...z, ...patch } : z));
  const removeZone = (i) => setZones((arr) => arr.filter((_, idx) => idx !== i));

  const doPrint = () => {
    if (!enabled) { toast.error("Primero activa el servicio y guarda"); return; }
    if (!waUrl) { toast.error("Configura el WhatsApp primero"); return; }
    window.print();
  };

  const downloadPng = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `chalann-domicilio-${(store?.name || "tienda").replace(/\s+/g, "_")}.png`;
    a.click();
  };

  if (loading) return (
    <main className="min-h-screen bg-[#050505] text-white">
      <DashNav />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 text-center text-white/40 text-sm">Cargando…</div>
    </main>
  );

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="domicilio">
      <div className="grain-overlay print:hidden" />
      <div className="print:hidden"><DashNav /></div>

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-8 md:py-12 print:py-0 print:px-0 print:max-w-full">
        {/* ---------- SCREEN VIEW ---------- */}
        <div className="print:hidden">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-2">/ Ventas a domicilio</p>
            <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-none mb-3">
              Pedidos por <span className="text-[#25D366]">WhatsApp</span>.
            </h1>
            <p className="text-white/60 max-w-2xl">
              Genera un QR único de tu tienda. Cuando un cliente lo escanea, se abre WhatsApp con un mensaje listo para que te escriba su pedido a domicilio.
            </p>
          </motion.div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Config panel */}
            <section className="lg:col-span-7 bg-[#0a0a0a] border border-white/10 rounded-3xl p-6 md:p-8" data-testid="domicilio-config">
              <div className="flex items-center justify-between mb-6">
                <div className="chip"><Truck size={12} className="text-[#25D366]" /> Configuración</div>
                <button
                  onClick={() => setEnabled((v) => !v)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-mono uppercase tracking-widest transition-colors ${
                    enabled ? "border-[#25D366]/60 bg-[#25D366]/15 text-[#25D366]" : "border-white/15 text-white/50"
                  }`}
                  data-testid="domicilio-toggle"
                >
                  {enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                  {enabled ? "Activo" : "Inactivo"}
                </button>
              </div>

              <label className="block mb-4">
                <span className="block font-mono text-[10px] tracking-widest text-white/50 uppercase mb-2">WhatsApp del negocio</span>
                <div className="flex items-stretch gap-2">
                  <span className="inline-flex items-center px-3 rounded-2xl bg-black/60 border border-white/10 font-mono text-sm text-white/60">+52</span>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 12))}
                    placeholder="10 dígitos"
                    inputMode="numeric"
                    className="input flex-1 font-mono"
                    data-testid="domicilio-phone"
                  />
                </div>
                <span className="mt-1 block text-[10px] text-white/40">Prefijo México automático. Para otro país, escribe los 12 dígitos.</span>
              </label>

              <label className="block mb-4">
                <span className="block font-mono text-[10px] tracking-widest text-white/50 uppercase mb-2">Mensaje del cliente (pre-llenado)</span>
                <textarea
                  value={greeting}
                  onChange={(e) => setGreeting(e.target.value)}
                  rows={3}
                  placeholder="Hola, quiero un pedido a domicilio de mi tienda. Mi pedido:"
                  className="input resize-none"
                  data-testid="domicilio-greeting"
                />
              </label>

              <label className="block mb-6">
                <span className="block font-mono text-[10px] tracking-widest text-white/50 uppercase mb-2">Horario de servicio</span>
                <input
                  value={schedule}
                  onChange={(e) => setSchedule(e.target.value)}
                  placeholder="Lun a Sáb 9am a 8pm"
                  className="input"
                  data-testid="domicilio-schedule"
                />
              </label>

              {/* Zonas de entrega */}
              <div className="mb-6" data-testid="delivery-zones-editor">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-mono text-[10px] tracking-widest text-white/50 uppercase">Zonas de entrega</p>
                    <p className="text-white/40 text-xs mt-0.5">Colonias y precios que verá el cliente antes de escribir por WhatsApp.</p>
                  </div>
                  <button type="button" onClick={addZone} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#25D366]/40 text-[#25D366] text-[10px] font-mono uppercase tracking-widest hover:bg-[#25D366]/10" data-testid="add-zone">
                    <Plus size={12} /> Agregar zona
                  </button>
                </div>
                {zones.length === 0 ? (
                  <p className="text-white/40 text-xs italic bg-white/[0.02] border border-white/10 rounded-2xl p-3">
                    Sin zonas por ahora. El QR abrirá WhatsApp directo. Agrega colonias para que el cliente vea el costo antes.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {zones.map((z, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center bg-white/[0.02] border border-white/10 rounded-2xl p-3" data-testid={`zone-${i}`}>
                        <div className="col-span-12 md:col-span-4 flex items-center gap-2">
                          <MapPin size={14} className="text-[#25D366] flex-shrink-0" />
                          <input
                            value={z.colonia}
                            onChange={(e) => updateZone(i, { colonia: e.target.value })}
                            placeholder="Nombre de la colonia"
                            className="input"
                            data-testid={`zone-colonia-${i}`}
                          />
                        </div>
                        <label className="col-span-4 md:col-span-2 text-[10px] font-mono uppercase tracking-widest text-white/40">
                          Envío $
                          <input
                            type="number" step="0.5" min="0"
                            value={z.price}
                            onChange={(e) => updateZone(i, { price: e.target.value })}
                            className="input mt-1 font-mono"
                            data-testid={`zone-price-${i}`}
                          />
                        </label>
                        <label className="col-span-4 md:col-span-2 text-[10px] font-mono uppercase tracking-widest text-white/40">
                          Mín. pedido
                          <input
                            type="number" step="10" min="0"
                            value={z.min_order}
                            onChange={(e) => updateZone(i, { min_order: e.target.value })}
                            className="input mt-1 font-mono"
                            data-testid={`zone-min-${i}`}
                          />
                        </label>
                        <input
                          value={z.notes || ""}
                          onChange={(e) => updateZone(i, { notes: e.target.value })}
                          placeholder="Nota (ej. 'Sólo hasta 7pm')"
                          className="col-span-10 md:col-span-3 input text-xs"
                          data-testid={`zone-notes-${i}`}
                        />
                        <button type="button" onClick={() => removeZone(i)} className="col-span-2 md:col-span-1 text-white/30 hover:text-red-400 flex justify-center" data-testid={`zone-remove-${i}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={save} disabled={saving} className="flex-1 py-3 rounded-2xl bg-[#25D366] hover:bg-[#3ee27d] text-black font-display text-lg tracking-tighter flex items-center justify-center gap-2 disabled:opacity-60" data-testid="domicilio-save">
                  {saving ? "Guardando…" : (<><Save size={16} /> Guardar</>)}
                </button>
                <button onClick={doPrint} disabled={!enabled || !qrTarget} className="flex-1 py-3 rounded-2xl border border-white/15 text-white hover:bg-white hover:text-black font-display text-lg tracking-tighter flex items-center justify-center gap-2 disabled:opacity-40" data-testid="domicilio-print">
                  <Printer size={16} /> Imprimir cartel
                </button>
                <button onClick={downloadPng} disabled={!qrTarget} className="py-3 px-4 rounded-2xl border border-white/10 text-white/70 hover:text-white hover:border-white/30 flex items-center justify-center gap-2 disabled:opacity-40" data-testid="domicilio-download-png">
                  <MessageCircle size={16} /> PNG
                </button>
              </div>

              {settings && !enabled && (
                <p className="mt-4 text-xs text-white/40 italic">
                  Con el servicio inactivo el QR no se muestra a los clientes en el cartel imprimible.
                </p>
              )}
            </section>

            {/* Preview panel */}
            <section className="lg:col-span-5">
              <div className="bg-gradient-to-br from-[#0f0f0f] to-[#050505] border border-white/10 rounded-3xl p-6 md:p-8" data-testid="domicilio-preview">
                <div className="chip mb-4"><Sparkles size={12} className="text-[#ccff00]" /> Vista previa</div>
                {qrTarget ? (
                  <>
                    <div ref={qrRef} className="bg-white p-4 rounded-2xl w-fit mx-auto">
                      <QRCodeCanvas value={qrTarget} size={220} bgColor="#ffffff" fgColor="#050505" level="M" includeMargin={false} data-testid="domicilio-qr-canvas" />
                    </div>
                    <div className="mt-4 space-y-1 text-center">
                      <p className="font-display text-xl tracking-tight">{store?.name}</p>
                      {schedule && <p className="text-white/60 text-sm flex items-center justify-center gap-1"><Clock size={12} /> {schedule}</p>}
                      {waNumber && <p className="font-mono text-xs text-white/40"><Phone size={10} className="inline mr-1" />+{waNumber}</p>}
                      {zones.length > 0 && (
                        <p className="text-[10px] font-mono text-[#ccff00] uppercase tracking-widest mt-2" data-testid="domicilio-qr-mode">
                          QR muestra {zones.length} zona{zones.length !== 1 ? "s" : ""} antes de WhatsApp
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-center text-white/40 text-sm py-12">Agrega tu WhatsApp para generar el código QR.</p>
                )}
              </div>
            </section>
          </div>
        </div>

        {/* ---------- PRINT VIEW ---------- */}
        <div className="hidden print:block" data-testid="domicilio-print-sheet">
          <div className="text-center py-8 px-6" style={{ color: "#050505" }}>
            <p className="text-xs tracking-[0.3em] uppercase text-neutral-500 mb-2">CHALANN · Pedidos por WhatsApp</p>
            <h2 style={{ fontFamily: "inherit" }} className="text-3xl font-black tracking-tight mb-1">{store?.name}</h2>
            {schedule && <p className="text-lg text-neutral-700 mb-6">{schedule}</p>}

            <div className="flex items-center justify-center my-8">
              {qrTarget && (
                <div className="bg-white p-4 border-2 border-neutral-900 rounded-2xl">
                  <QRCodeCanvas value={qrTarget} size={320} bgColor="#ffffff" fgColor="#050505" level="M" includeMargin={false} />
                </div>
              )}
            </div>

            <p className="text-3xl md:text-4xl font-black tracking-tight uppercase" style={{ color: "#050505" }}>{LEGEND}</p>
            <p className="mt-3 text-neutral-700 text-lg">Escanea con la cámara de tu celular y mándanos tu pedido por WhatsApp.</p>

            {waNumber && (
              <p className="mt-8 text-neutral-500 text-sm">WhatsApp: +{waNumber}</p>
            )}
            <p className="mt-2 text-neutral-400 text-xs tracking-widest uppercase">Sistema CHALANN · Punto de venta por voz</p>
          </div>
        </div>
      </div>

      <Toaster theme="dark" position="top-center" />
    </main>
  );
}
